/**
 * The add-in's paragraph-naming pass: gives every body paragraph of the open
 * document a bookmark name the core can address, before every document fetch and
 * after the add-in inserts paragraphs.
 *
 * `nameEveryParagraphUnderTheActiveScheme` below picks the scheme per org: prod's
 * `_dioptra_p_` names, written here, or the proposal engine's names, written by
 * `paragraph-bookmarks-engine.ts`. Both read which paragraphs are already named
 * through `paragraph-bookmark-ownership.ts`.
 */
import { v4 as uuidv4 } from "uuid";

import { withWordContextWrite } from "./document";
import { readIdsOfParagraphsOwningABookmark } from "./paragraph-bookmark-ownership";
import { ParagraphNameMemory, nameParagraphsForEngine } from "./paragraph-bookmarks-engine";
import { ProposalEngineAccess } from "./proposal-engine-access";

const P_BOOKMARK_PREFIX = "_dioptra_p_";
const WORD_BOOKMARK_NAME_MAX_LENGTH = 40;

function insertFreshPBookmark(paragraph: Word.Paragraph): void {
    const suffix = uuidv4().replace(/-/g, "").slice(0, WORD_BOOKMARK_NAME_MAX_LENGTH - P_BOOKMARK_PREFIX.length);
    const bookmarkName = `${P_BOOKMARK_PREFIX}${suffix}`;
    const range = paragraph.getRange(Word.RangeLocation.content);

    range.insertBookmark(bookmarkName);
}

/**
 * Gives every body paragraph that owns no `_dioptra_p_` name one fresh name.
 *
 * SYNC ROUNDS, in order
 *   1. load the paragraphs and list every bookmark in the body
 *   2. find the first paragraph of each `_dioptra_p_` name  (`readIdsOfParagraphsOwningABookmark`)
 *   3. compare each name's range with that paragraph        (`readIdsOfParagraphsOwningABookmark`)
 *   4. insert the fresh names
 *
 * CASES, per paragraph
 *   - owns a `_dioptra_p_` name that lies on it    -> untouched
 *   - owns none, or only names that drifted away   -> one fresh name inserted on its content range
 */
const tryBookmarkingAllParagraphs = async (): Promise<void> => withWordContextWrite(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("uniqueLocalId");
    const documentBookmarkNames = context.document.body.getRange().getBookmarks(true, false);
    await context.sync();

    const pBookmarkNames = documentBookmarkNames.value.filter((bookmarkName) => bookmarkName.startsWith(P_BOOKMARK_PREFIX));
    const idsOfParagraphsOwningAPBookmark = await readIdsOfParagraphsOwningABookmark(context, pBookmarkNames);
    paragraphs.items
        .filter((paragraph) => !idsOfParagraphsOwningAPBookmark.has(paragraph.uniqueLocalId))
        .forEach(insertFreshPBookmark);
    await context.sync();
});

/**
 * Names every body paragraph, under whichever scheme the org's engine calls for.
 *
 * CASES
 *   - proposal engine on  -> `nameParagraphsForEngine`: the `_dioptra_p_` names this
 *     module writes are deleted and every paragraph is stamped `_word_word_word`,
 *     which is what the shared engine's reader, the web's naming pass and the
 *     operations prompt all expect
 *   - proposal engine off -> `tryBookmarkingAllParagraphs` below, untouched: prod's
 *     `_dioptra_p_` naming, written by this module and by the core's Python minters
 *
 * WHEN the flag lookup fails
 *   CONSEQUENCE
 *     `ProposalEngineAccess.current()` answers `enabled: false`, so a document is
 *     named prod's way rather than left unnamed.
 */
const paragraphNameMemory = new ParagraphNameMemory();

const nameEveryParagraphUnderTheActiveScheme = async (): Promise<void> => {
    const access = await ProposalEngineAccess.current();
    if (!access.enabled) return tryBookmarkingAllParagraphs();
    await withWordContextWrite(async (context) => {
        await nameParagraphsForEngine(context, paragraphNameMemory);
    });
};

export const bookmarkAllParagraphs = async (): Promise<void> => {
    const maxRetries = 3;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            await nameEveryParagraphUnderTheActiveScheme();
            return;
        } catch (error) {

            if (attempt < maxRetries) {
                console.warn(`Bookmark operation failed (attempt ${attempt}/${maxRetries}), retrying in ${100 * attempt}ms...`);

                await new Promise(resolve => setTimeout(resolve, 100 * attempt));
            } else if (attempt === maxRetries) {
                console.error(`Failed to bookmark paragraphs after ${maxRetries} attempts:`, error);
                throw error;
            }
        }
    }
};
